import { AppRole, OrganizationType } from '@craft/database';
import { type ExtractValues } from '@craft/lib';
import { type TitleCase, titleCase } from '@craft/lib/formatters';
import { isEnumMember } from '@craft/lib/primitives';

import {
  type AppRole as GraphQlAppRole,
  type OrganizationType as GraphQlOrganizationType,
} from 'connect-graphql/generated';

export type ToGraphQlAppRole<R extends AppRole> = (typeof GraphQlAppRole)[TitleCase<R>];

export const isAppRole = (role: unknown): role is AppRole | GraphQlAppRole =>
  isEnumMember(role, AppRole);

export const isOrgType = (
  orgType: unknown,
): orgType is GraphQlOrganizationType | OrganizationType => isEnumMember(orgType, OrganizationType);

export const EvaluatingAppRoles = [AppRole.ADMIN, AppRole.EVALUATOR, AppRole.INSTRUCTOR] as const;

export type EvaluatingAppRole = (typeof EvaluatingAppRoles)[number];

export function isEvaluatingAppRole(role: AppRole): role is EvaluatingAppRole;

export function isEvaluatingAppRole(
  role: GraphQlAppRole,
): role is Extract<GraphQlAppRole, EvaluatingAppRole>;

export function isEvaluatingAppRole(role: AppRole | GraphQlAppRole) {
  return EvaluatingAppRoles.includes(role as EvaluatingAppRole);
}

/**
 * Returns the associated {@link GraphQlAppRole} enum value for the given {@link AppRole} value
 * in a type-safe manner.
 *
 * The method dynamically imports the actual value of the {@link GraphQlAppRole} enum from the
 * generated GraphQL types because this file is sometimes accessed before those generated types
 * are generated.  Importing the type of the enum:
 *
 * import { type AppRole } from '@/generated/graphql';
 *
 * is okay - but this method needs to import the actual value of the enum, which has to be done
 * dynamically.
 */
export const toGraphQlAppRole = <R extends AppRole>(role: R): ToGraphQlAppRole<R> => {
  /* eslint-disable-next-line @typescript-eslint/no-require-imports,
     @typescript-eslint/no-require-imports */
  const { AppRole: AppRoleEnum } = require('connect-graphql/generated') as {
    AppRole: GraphQlAppRole;
  };

  const enumMember: TitleCase<ExtractValues<typeof AppRole>> = titleCase(role, true);

  if (!AppRoleEnum[enumMember as keyof typeof AppRoleEnum]) {
    throw new Error(
      `The key '${enumMember}' is not a valid member of the GraphQL generated AppRole enum!`,
    );
  }

  return AppRoleEnum[
    enumMember as keyof typeof AppRoleEnum
  ] as (typeof GraphQlAppRole)[TitleCase<R>];
};
